import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../auth';
import { useProject } from '../projects';
import {
  UserAvatar,
  ModuleDropdown,
  ModeSegmentedControl,
  DataHubModal,
  UrbanNetworkLoaderV2,
} from './components';
import { ChatAIButton, ChatPanel } from '../chat';
import { CommandPalette, useCommandPaletteStore } from '../command-palette';
import './map-layout.css';

const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.userAgent);

/**
 * TopBar MVP - Barra superior compacta de 56px
 *
 * @version 7.0 (MVP)
 * @module core/layouts
 * @updated 2026-04-11
 *
 * @description
 * Barra de navegación horizontal compacta para MapLayout.
 * Responde a: "¿Dónde estoy y qué contexto de trabajo tengo activo?"
 *
 * @remarks
 * **Estructura MVP**:
 * [Logo] [Módulo ▼] [Proyecto] [CommandBar] [Modalidad] [Usuario]
 *
 * **Altura**: 56px (reducida desde 120px)
 *
 * **Elementos**:
 * - Logo: Identidad, link a dashboard
 * - Módulo: Dropdown compacto con categorías
 * - Proyecto: Nombre del proyecto activo
 * - Datos: Acceso al hub de import/export
 * - CommandBar: Búsqueda global
 * - Modalidad: Segmented control (E|I|A|P|O)
 * - Usuario: Avatar con menú (UIScale, Idioma, Logout)
 *
 * @remarks
 * UIScale y selector de idioma están en el menú del Avatar, no en el TopBar directamente.
 * Chat IA es un FAB flotante (.chat-fab-container), renderizado fuera del header.
 */
interface TopBarProps {
  /** Modos visibles en el segmented control. Si no se pasa, muestra todos. */
  visibleModes?: import('../operational-modes').OperationalMode[];
}

export function TopBar({ visibleModes }: TopBarProps = {}): JSX.Element {
  const { user, logout } = useAuthStore();
  const { projectId, availableProjects } = useProject();
  const navigate = useNavigate();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isDataHubOpen, setIsDataHubOpen] = useState(false);
  const { open: openPalette } = useCommandPaletteStore();

  // Obtener el nombre del proyecto activo
  const activeProject = availableProjects.find(p => p.id === projectId);
  const projectName = activeProject?.name ?? null;

  /**
   * Maneja el evento de logout
   */
  const handleLogout = useCallback((): void => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const handleOpenDataHub = useCallback((): void => {
    setIsDataHubOpen(true);
  }, []);

  const handleToggleChat = useCallback((): void => {
    setIsChatOpen(c => !c);
  }, []);

  /**
   * Determina la URL del home según el rol del usuario
   */
  const homeUrl = (): string => {
    const isSuperAdmin = user?.role?.name === 'superadmin';
    return isSuperAdmin ? '/admin/projects' : '/dashboard';
  };

  return (
    <>
      <header className="map-topbar map-topbar-mvp" role="banner" aria-label="Barra de navegación principal">
        {/* === GRUPO IZQUIERDO: Logo + Módulo + Proyecto === */}
        <div className="topbar-left">
          {/* Logo WESTEER — Sora brand */}
          <Link to={homeUrl()} className="topbar-logo" aria-label="Ir a la página principal">
            <UrbanNetworkLoaderV2 size="xs" />
            <span className="topbar-wordmark">WESTEER</span>
          </Link>

          {/* Selector de módulo (dropdown) — incluye acceso a Datos vía menú contextual */}
          <ModuleDropdown onOpenDataHub={handleOpenDataHub} />

          {/* Nombre del proyecto */}
          {projectName && (
            <span className="topbar-project" title={projectName}>
              {projectName}
            </span>
          )}
        </div>

        {/* === GRUPO CENTRAL: Launcher de búsqueda → abre CommandPalette === */}
        <div className="topbar-center-mvp">
          <button
            type="button"
            className="topbar-search-launcher"
            onClick={openPalette}
            aria-label="Abrir búsqueda global"
          >
            <svg
              className="topbar-search-launcher-icon"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <span className="topbar-search-launcher-text">Buscar o ejecutar…</span>
            <kbd className="topbar-search-launcher-kbd">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
          </button>
        </div>

        {/* === GRUPO DERECHO: Modalidad + Controles === */}
        <div className="topbar-right">
          {/* Selector de modalidad (segmented control) */}
          <ModeSegmentedControl showLabels={false} showIcons={false} visibleModes={visibleModes} />

          {/* Controles de usuario */}
          <div className="topbar-controls">
            {user && <UserAvatar user={user} onLogout={handleLogout} />}
          </div>
        </div>
      </header>

      {/* Chat IA — FAB flotante sobre el mapa (bottom-right) */}
      {/* chat-fab-open: oculta el FAB cuando el panel está abierto para evitar redundancia */}
      <div className={`chat-fab-container${isChatOpen ? ' chat-fab-open' : ''}`}>
        <ChatAIButton
          onClick={handleToggleChat}
          isActive={isChatOpen}
        />
      </div>

      {/* Chat Panel */}
      <ChatPanel
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
      />

      <DataHubModal
        isOpen={isDataHubOpen}
        onClose={() => setIsDataHubOpen(false)}
        projectId={projectId}
        projectName={projectName}
      />

      {/* Command Palette — Fase 2C: acciones rápidas locales integradas */}
      <CommandPalette
        onOpenDataHub={handleOpenDataHub}
        onToggleChat={handleToggleChat}
      />
    </>
  );
}
